import React, { useEffect, useRef, useState } from "react";
import styles from "./Feedback.module.scss";
import axios from "axios";
import { useParams, useSearchParams } from "react-router-dom";
import Constants from "../../Constants/Constants";
import FullScreenLoader from "../../Components/FullScreenLoader/FullScreenLoader";
import InlineLoader from "../../Components/InlineLoader/InlineLoader";
import ThumbUpLogo from "../../Assets/images/thumb-logo.png";
import thumbDownLogo from "../../Assets/images/thumb-down-logo.png";
import toast from "react-hot-toast";

function Feedback() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const initialRating = searchParams.get("initialRating");
  const [loading, setLoading] = useState(true);
  const [formSubmitLoading, setFormSubmitLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [text, setText] = useState<string>("");
  const [rating, setRating] = useState<string>("");
  const [error, setError] = useState("");
  const [userData, setUserData] = useState<{ jobs: any[]; session: any }>({
    jobs: [],
    session: {},
  });
  const timerRef = useRef<number | null>(null);

  const recordEmailLog = async (ratingValue: string) => {
    if (!ratingValue) {
      return;
    }

    try {
      await axios.post(`${Constants.apiUrl}/feedback/recordEmailLog`, {
        token,
        rating: ratingValue,
        feedback: text,
      });
    } catch (error) {}
  };

  const validateToken = async (tokenString: string) => {
    try {
      const { data } = await axios.post(
        `${Constants.apiUrl}/feedback/validateToken`,
        {
          token: tokenString,
        }
      );
      if (data?.success === true) {
        setLoading(false);

        setUserData(data?.data);
      } else {
        throw Error("");
      }
    } catch (error: any) {
      if (error?.response?.data) {
        setError(error?.response?.data?.message);
      } else {
        setError("An error occured, Please try again later");
      }
      setLoading(false);
    }
  };

  const formSubmitHandler = async (
    event: React.MouseEvent<HTMLButtonElement>,
    tokenString: string
  ) => {
    event.preventDefault();

    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    try {
      if (rating && text) {
        if (!formSubmitLoading) {
          setFormSubmitLoading(true);
          const { data } = await axios.post(
            `${Constants.apiUrl}/feedback/submit`,
            {
              token: tokenString,
              feedback: text,
              rating,
              sessionId: userData?.session?.sessionId,
            }
          );

          if (data?.success === true) {
            setFormSubmitLoading(false);
            setFormSubmitted(true);
            toast.success("Feedback submitted successfully");
          } else {
            throw Error("");
          }
        }
      } else {
        if (!rating) {
          toast.error("Please select reaction");
        } else if (!text) {
          toast.error("Text field cannot be empty");
        }
      }
    } catch (error: any) {
      if (error?.response?.data) {
        setError(error?.response?.data?.message);
      } else {
        setError("An error occured, Please try again later");
      }
      setFormSubmitLoading(false);
    }
  };

  const ratingCickHandler = async (value: "thumbDown" | "thumbUp") => {
    setRating(value);
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    try {
      const { data } = await axios.post(
        `${Constants.apiUrl}/feedback/recordLog`,
        {
          token,
          rating: value,
          sessionId: userData?.session?.sessionId,
        }
      );

      setUserData(data?.data);
      timerRef.current = window.setTimeout(async () => {
        await recordEmailLog(value);
      }, 30000);
    } catch (error) {}
  };

  useEffect(() => {
    validateToken(token || "");
    if (initialRating) {
      setRating(initialRating);
    }
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, [token, initialRating]);

  if (loading) {
    return <FullScreenLoader />;
  }

  return (
    <div className={styles.mainContainer}>
      {error && (
        <div className={styles.errorContainer}>
          <p className={styles.error}>{error}</p>
        </div>
      )}
      {formSubmitted && (
        <div className={styles.formSubmittedContainer}>
          {rating === "thumbUp" ? (
            <>
              <p>Thank you for your response</p>
              <p>
                We will continue to update you on the progress of your project
              </p>
              <p>
                Please visit{" "}
                <a href={window.location.origin}>{window.location.origin}</a> to
                view the latest status of your project at any time
              </p>
            </>
          ) : (
            <>
              <p>
                We are concerned that you are unhappy with your current
                experience. We have escalated your response to a dedicated
                support team for immediate attention.
              </p>
              <p>Thank you for your feedback, our team will contact you soon</p>
              <p>
                For faster service, you may contact our Customer Relations
                Department by calling{" "}
                <a href="tel:1-800-410-7274">1-800-410-7274</a>
              </p>
            </>
          )}
        </div>
      )}
      {!formSubmitted && !error && (
        <div className={styles.bodyContainer}>
          {userData?.jobs?.length > 0 && (
            <div className={styles.informationContainer}>
              <div className={styles.headingContainer}>
                <h3 className={styles.heading}>Job Status</h3>
              </div>

              {userData?.jobs?.map((job: any, i: number) => {
                return (
                  <React.Fragment key={i}>
                    <div className={styles.lineBreak}></div>
                    <div className={styles.statusContainer}>
                      <div className={styles.status}>
                        <span>Name:</span> <span>{job?.name}</span>
                      </div>
                      <div className={styles.status}>
                        <span>RRT Job Number:</span> <span>{job?.number}</span>
                      </div>
                      <div className={styles.status}>
                        <span>Address:</span> <span>{job?.address}</span>
                      </div>
                    </div>
                    <div className={styles.statusContainer}>
                      <div className={styles.status}>
                        <span>Job Number:</span> <span>{job?.claimNumber}</span>
                      </div>
                      <div className={styles.status}>
                        <span>Project Manager:</span>{" "}
                        <span>{job?.userSettingEmployeeType}</span>
                      </div>
                      <div className={styles.status}>
                        <span>Status:</span>{" "}
                        <span>{job?.alternativeStatus}</span>
                      </div>
                    </div>
                    {userData?.jobs?.length - 1 === i && (
                      <div className={styles.lineBreak}></div>
                    )}
                  </React.Fragment>
                );
              })}

              <div className={styles.descriptionContainer}>
                <p>Tell us how your experience with Rapid Response is going.</p>
                <p>Your response is confidential.</p>
              </div>
            </div>
          )}

          <form className={styles.formContainer}>
            <div className={styles.emojiContainer}>
              <div className={styles.emoji}>
                <img
                  className={
                    rating === "thumbUp" ? styles.activeImage : styles.image
                  }
                  src={ThumbUpLogo}
                  onClick={() => ratingCickHandler("thumbUp")}
                  alt="ThumbUpLogo"
                />
              </div>

              <div className={styles.emoji}>
                <img
                  className={
                    rating === "thumbDown" ? styles.activeImage : styles.image
                  }
                  src={thumbDownLogo}
                  onClick={() => ratingCickHandler("thumbDown")}
                  alt="thumbDownLogo"
                />
              </div>
            </div>
            <div className={styles.textAreaContainer}>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                className={styles.textArea}
                placeholder="Please write your review"
              />
            </div>
            {formSubmitLoading && <InlineLoader />}
            <div className={styles.buttonContainer}>
              <button
                className={styles.button}
                type="submit"
                onClick={(e) => formSubmitHandler(e, token || "")}
              >
                Submit Form
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// Can they submit the same form again on same link or it will expire after one time form submission
// use effect check if its valid id and if not submkitted allow the user to submit again
// My suggestions

export default Feedback;
